import { UploadResult } from "client/Api/Documents/common";
import {
	cancelUpload,
	ChunkUploadModel,
	fileUpload,
	FileUploadModel
} from "client/Api/Documents/SharePoint/sharePointFileContent";
import { isNil } from "client/Misc/utils";
import { publish, SharePointEvent } from "client_legacy/Documents/SharePoint/documentEvents";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { Folderish } from "client_legacy/Documents/SharePoint/models";
import {
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef
} from "react";
import {
	UploadBatchHandlers,
	UploadChunkInfo,
	UploadFileInfo,
	UploaderItemResult,
	UploaderUploadedFileActions
} from "../DocumentUploader/useDocumentUploader";
import { HttpRequestError } from "client/Api/httpClient";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import {
	DocumentsViewModel,
	FileViewModel,
	FolderViewModel,
	ListLocationModel
} from "client/Api/Documents/SharePoint/common";
import { reduceToAvailableActions } from "client_legacy/ndaw/actions";
import {
	AlertManager,
	useAlerts
} from "client/Hooks";
import { UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { useEditorPreference } from "../useEditorPreference";
import {
	getOpenActions,
	OpenActionHandlers,
	SharePointDocumentAction,
	SharePointDocumentActionContext
} from "./actions";
import { SyncClient } from "client/Api/SharePointSettings/sharepointSettings";

export type SharePointDocumentUploaderHandlers<TFolder extends Folderish | undefined> =
	TFolder extends Folderish ? UploadBatchHandlers :
	TFolder extends undefined ? undefined :
	never;

export function useSharePointDocumentUploaderHandlers<TFolder extends Folderish | undefined>(
	view: DocumentsViewModel,
	folder: Folderish | undefined,
	doesSupportChunking: boolean,
	onOpenFolder: (folder: Folderish) => void
): SharePointDocumentUploaderHandlers<TFolder> {
	const wasWindowUnloadedRef: RefObject<boolean> = useRef<boolean>(false);

	const alertManager: AlertManager = useAlerts();
	const editorPreference: UserEditorPreference = useEditorPreference();

	// With concurrency it can happen that multiple requests tell they created the top folder while in fact
	// they did not (some other concurrent request did), so we filter it on the frontend. The reason for this
	// duplication is due to the optimization done in #32612 where we no longer have a hard guarantee whether
	// a folder existed or was created
	const broadcastedCreatedTopFolderIdsRef: ReadonlyRefObject<Set<string>> = useRef<Set<string>>(
		new Set<string>()
	);

	const notifyAboutCreatedTopFolder = useCallback(
		(createdTopFolder: FolderViewModel): void => {
			const { current: broadcastedCreatedTopFolderIds } = broadcastedCreatedTopFolderIdsRef;
			const id: string = getFolderUniqueId(createdTopFolder);

			if (!broadcastedCreatedTopFolderIds.has(id)) {
				broadcastedCreatedTopFolderIds.add(id);
				publish(SharePointEvent.AddedOrReplaced, createdTopFolder);
			}
		},
		[]
	);

	const getOpenFileAction = useCallback(
		(
			uploadedFile: FileViewModel,
			folder: Folderish
		): (() => void) | undefined => {
			const context: SharePointDocumentActionContext = {
				view,
				folder,
				selection: [uploadedFile],

				// Default values for properties which we
				// don't need in the uploader
				isInSearchResults: false,
				isInSelectMode: false,
				isMobile: false,
				isSharingViaLinkEnabled: false,
				isVersioningEnabled: false,
				maxNumberOfFilesToDownloadAsZip: 0,
				syncClient: SyncClient.None
			};

			const handlers: OpenActionHandlers = {
				openFolder: (folder: FolderViewModel): void => {
					onOpenFolder({ ...folder, isRoot: false });
				},
				selectItem: (): void => {
					/** Nope */
				}
			};

			const actions: readonly SharePointDocumentAction[] = getOpenActions(
				handlers,
				[uploadedFile],
				alertManager,
				editorPreference
			);

			const result: SharePointDocumentAction | undefined = reduceToAvailableActions(actions, context)[0];

			return result
				? (): void => result.execute(context)
				: undefined;
		},
		[
			alertManager,
			editorPreference,
			view,
			onOpenFolder
		]
	);

	const uploadFileHandler = useCallback(
		async (fileInfo: UploadFileInfo, signal: AbortSignal): Promise<UploaderItemResult> => {
			if (isNil(folder)) {
				throw new Error("Location (Folder) is not set but a document is uploaded");
			}

			const data: FileUploadModel = convertFileToUploadModel(fileInfo);
			const uploadedFileActions: UploaderUploadedFileActions = {};

			try {
				const result: UploadResult = await fileUpload(
					convertToLocationModel(folder.location),
					folder.id,
					data,
					signal
				);

				if (result.success) {
					const { uploadedFile } = result;
					publish(SharePointEvent.AddedOrReplaced, uploadedFile);

					if (uploadedFile) {
						const openFile: (() => void) | undefined = getOpenFileAction(
							uploadedFile,
							// The actual file folder isn't needed for opening operations, so we use the current folder
							folder
						);

						uploadedFileActions.openFile = openFile;
					}
				}

				if (result.createdTopFolder) {
					notifyAboutCreatedTopFolder(result.createdTopFolder);
				}

				return convertResult(
					result,
					uploadedFileActions
				);
			} catch(e) {
				const message: string | undefined = getErrorMessageFromHttpResponse(e);

				return {
					kind: "failed",
					message
				};
			}
		},
		[
			folder,
			notifyAboutCreatedTopFolder,
			getOpenFileAction
		]
	);

	const uploadChunkHandler = useCallback(
		async (
			chunkInfo: UploadChunkInfo,
			signal: AbortSignal
		): Promise<UploaderItemResult> => {
			if (isNil(folder)) {
				throw new Error("Location (Folder) is not set but a document is uploaded");
			}

			const data: ChunkUploadModel = convertChunkToUploadModel(chunkInfo);
			const uploadedFileActions: UploaderUploadedFileActions = {};

			const {
				partIndex,
				totalParts
			} = chunkInfo;

			const isFirstChunk: boolean = partIndex === 0;
			const isLastChunk: boolean = partIndex + 1 === totalParts;

			try {
				const result: UploadResult = await fileUpload(
					convertToLocationModel(folder.location),
					folder.id,
					data,
					signal
				);

				if (isFirstChunk && result.createdTopFolder) {
					notifyAboutCreatedTopFolder(result.createdTopFolder);
				}

				if (isLastChunk && result.success) {
					const { uploadedFile } = result;
					publish(SharePointEvent.AddedOrReplaced, uploadedFile);

					if (uploadedFile) {
						const openFile: (() => void) | undefined = getOpenFileAction(
							uploadedFile,
							// The actual file folder isn't needed for opening operations, so we use the current folder
							folder
						);

						uploadedFileActions.openFile = openFile;
					}
				}

				return convertResult(result, uploadedFileActions);
			} catch (e) {
				const message: string | undefined = getErrorMessageFromHttpResponse(e);

				return {
					kind: "failed",
					message
				};
			}
		},
		[
			folder,
			notifyAboutCreatedTopFolder,
			getOpenFileAction
		]
	);

	const cancelChunkedUploadHandler = useCallback(
		async (
			fileUploadGuid: Guid,
			fileName: string,
			path: string
		): Promise<void> => {
			if (isNil(folder)) {
				throw new Error("Location (Folder) is not set but a document is uploaded");
			}

			const folderLocation: ListLocationModel = convertToLocationModel(folder.location);

			await cancelUpload(
				folderLocation,
				folder.id,
				fileName,
				fileUploadGuid,
				path,
				!wasWindowUnloadedRef.current
			);
		},
		[folder]
	);

	useEffect(
		(): (() => void) => {
			const controller = new AbortController();
			const { signal } = controller;

			window.addEventListener(
				"unload",
				() => {
					wasWindowUnloadedRef.current = true;
				},
				{ signal }
			);

			return (): void => {
				controller.abort();
			};
		},
		[]
	);

	return useMemo(
		(): SharePointDocumentUploaderHandlers<TFolder> => {
			if (!folder) {
				return undefined as SharePointDocumentUploaderHandlers<TFolder>;
			}

			const result: UploadBatchHandlers = doesSupportChunking
				? {
					cancelChunkedUpload: cancelChunkedUploadHandler,
					uploadChunk: uploadChunkHandler,
					uploadFile: uploadFileHandler
				}
				: {
					uploadFile: uploadFileHandler
				};

			return result as SharePointDocumentUploaderHandlers<TFolder>;
		},
		[
			cancelChunkedUploadHandler,
			doesSupportChunking,
			folder,
			uploadChunkHandler,
			uploadFileHandler
		]
	);
}

function convertFileToUploadModel(seedData: UploadFileInfo): FileUploadModel {
	const {
		file,
		fileGuid,
		fileName,
		path,
		nameConflictResolution
	} = seedData;

	return {
		file,
		fileName,
		fileGuid,
		path,
		nameConflictResolution
	};
}

function convertChunkToUploadModel(seedData: UploadChunkInfo): ChunkUploadModel {
	const {
		chunk,
		fileGuid,
		fileName,
		path,
		nameConflictResolution,
		partIndex,
		partByteOffset,
		totalParts
	} = seedData;

	return {
		file: chunk,
		fileGuid,
		fileName,
		path,
		nameConflictResolution,
		partIndex,
		partByteOffset,
		totalParts,
	};
}

function convertResult(
	uploadResult: UploadResult,
	uploadedFileActions: UploaderUploadedFileActions
): UploaderItemResult {
	if (uploadResult.success) {
		return {
			kind: "success",
			uploadedFileName: uploadResult.uploadedFileName,
			uploadedFileActions: uploadedFileActions
		};
	}

	if (uploadResult.isNamingConflict) {
		return {
			kind: "name-conflict",
			message: uploadResult.error
		};
	}

	return {
		kind: "failed"
	};
}

function getErrorMessageFromHttpResponse(error: unknown): string | undefined {
	if (!(error instanceof HttpRequestError)) {
		return undefined;
	}

	switch (error.statusCode) {
		case 413:
			return localize("_FileSizeTooLarge");
		default:
			return undefined;
	}
}

function getFolderUniqueId(
	folder: FolderViewModel
): string {
	const {
		location
	} = folder;

	return `${location.siteUrl}_${location.listId}_${folder.id}`;
}